import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const PROJECTS = [
  { id: 1, title: 'ATLAS RESIDENCE', status: 'Satışta', location: 'İstanbul', img: '/hero-atlas.jpg' },
  { id: 2, title: 'TURKUAZ KULELER', status: 'Anahtar Teslim', location: 'Küçükçekmece', img: '/yapsat-site-2.png' },
  { id: 3, title: 'KORU VADİ', status: 'Yapım Aşamasında', location: 'Florya', img: '/yapsat-int-4.png' },
  { id: 4, title: 'MARINE PARK', status: 'Satışta', location: 'Beylikdüzü', img: '/tk-ext-wide-1.png' },
  { id: 5, title: 'ROYAL ÇAMLIK', status: 'Projenlendiriliyor', location: 'Bahçeşehir', img: '/yapsat-site-4.png' },
];

export default function HorizontalProjectGallery() {
  const targetRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Mobil ekran tespiti
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const x = useTransform(smoothProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section ref={targetRef} className="relative md:h-[250vh] bg-zinc-900 py-16 md:py-0">
      <div className="md:sticky top-0 md:h-screen flex flex-col justify-center overflow-hidden md:py-24">
        
        {/* Başlık Alanı */}
        <div className="max-w-7xl mx-auto px-6 w-full mb-8 md:mb-12 flex justify-between items-end relative z-10">
          <div>
            <div className="text-teal-400 text-xs md:text-sm font-bold tracking-[0.2em] md:tracking-[0.3em] uppercase mb-3 md:mb-4 flex items-center gap-3 md:gap-4">
              <div className="w-8 md:w-12 h-px bg-teal-400"></div> İmza Projeler
            </div>
            <h2 className="text-3xl md:text-6xl font-black text-white tracking-tight leading-tight" style={{ fontFamily: 'Raleway, sans-serif' }}>
              Yaşam Alanı <br/> Sanatı.
            </h2>
          </div>
          <Link to="/catalog" className="hidden md:inline-flex items-center gap-2 text-white border-b border-white/30 pb-1 hover:border-white transition-colors">
            Tüm Kataloğu İncele <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Yatay Kayan Galeri (Mobilde Native Scroll & Snap) */}
        <motion.div 
          style={isMobile ? {} : { x }} 
          className="flex gap-4 md:gap-8 px-6 md:px-24 overflow-x-auto md:overflow-visible snap-x snap-mandatory pb-8 md:pb-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          {PROJECTS.map((project, idx) => (
            <motion.div 
              key={project.id}
              className="relative w-[85vw] md:w-[60vw] lg:w-[45vw] h-[40vh] md:h-[60vh] flex-shrink-0 group overflow-hidden rounded-2xl cursor-pointer snap-center shadow-2xl"
              whileHover={{ scale: 0.98 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              {/* Resim */}
              <motion.img 
                src={project.img} 
                alt={project.title}
                className="w-full h-full object-cover md:grayscale-[30%] group-hover:grayscale-0 transition-all duration-700"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.7 }}
              />
              
              {/* Karartma */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity duration-500"></div>
              
              {/* İçerik */}
              <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between">
                <div className="flex justify-between items-start">
                  <div className="bg-red-600 text-white text-[10px] md:text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider backdrop-blur-md">
                    {project.status}
                  </div>
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border border-white/30 flex items-center justify-center text-white backdrop-blur-md md:group-hover:bg-white md:group-hover:text-black transition-all transform md:translate-x-4 md:opacity-0 md:group-hover:translate-x-0 md:group-hover:opacity-100">
                    <i className="ri-arrow-right-up-line text-lg md:text-xl"></i>
                  </div>
                </div>
                
                <div className="transform md:translate-y-4 md:group-hover:translate-y-0 transition-transform duration-500">
                  <div className="flex items-center gap-2 text-white/70 mb-1 md:mb-2 text-xs md:text-sm font-medium">
                    <i className="ri-map-pin-line"></i> {project.location}
                  </div>
                  <h3 className="text-2xl md:text-4xl font-black text-white tracking-tight drop-shadow-lg" style={{ fontFamily: 'Raleway, sans-serif' }}>
                    {project.title}
                  </h3>
                </div>
              </div>

              {/* Tıklanabilir Link Katmanı */}
              <Link to="/catalog" className="absolute inset-0 z-20" aria-label={`${project.title} Projesini İncele`}></Link>
            </motion.div>
          ))}
        </motion.div>

        {/* Mobil Buton */}
        <div className="md:hidden mt-4 px-6 relative z-10 flex justify-center w-full">
          <Link to="/catalog" className="inline-flex items-center gap-2 text-white bg-white/5 border border-white/20 px-8 py-3 rounded-xl hover:bg-white/10 transition-colors w-full justify-center">
            Tüm Projeler <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

      </div>
    </section>
  );
}
